import { useContext, useEffect, useState } from 'react';
import useBookingTracking from './useBookingTracking';
import { graphqlSdk } from '../Utils/graphql-client';
import usePageType from './use-page-type';
import {
  bookingState,
  BookingStateCombinedState,
} from '../Pages/Booking/client-state/booking-state-context';
import {
  bookingEditState,
  BookingEditStateCombinedState,
} from '../Pages/BookingEdit/client-state/booking-edit-state-context';
import { orderReducerActionTypes } from '../redusers/order-reducer';
import { isSummaryOpenActionTypes } from '../redusers/is-summary-open-reducer';
import useBookingEditTracking from './use-booking-edit-tracking';
import useBookingShop from '../Pages/Booking/server-data/use-booking-shop';
import useBookingEditShop from '../Pages/BookingEdit/server-data/use-booking-edit-shop';
import {
  AddonsErrorCode,
  Booking_GetShopQuery,
  BookingEdit_GetBookingQuery,
} from '../Utils/graphql-sdk';
import useTypeSafeLocalize from './use-type-safe-localize';
import { ErrorLocalization, LocalizationCategory } from 'localizations/types';

const checkErrorCode = (error: unknown, errorCode: AddonsErrorCode) => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'errors' in error.response &&
    Array.isArray(error.response.errors) &&
    error.response.errors[0]?.extensions?.code === errorCode
  ) {
    return true;
  } else {
    return false;
  }
};

interface Item {
  itemId: string;
  alcoholUnits?: number;
}

interface StateOrder {
  itemId: string;
  quantity: number;
}

const validateAlcoholUnits = (
  boatCount: number,
  alcoholUnits: number,
  existingUnits: number
) => {
  const numberOfPeople = boatCount * 8;
  const maxUnits = numberOfPeople * 2;
  return existingUnits + alcoholUnits <= maxUnits;
};

const useAddItem = (item: Item) => {
  const [itemCount, setItemCount] = useState(1);
  const [loading, setLoading] = useState(false);
  // here we can get into showing more informative error messages
  // such as out of stock, not enough time etc
  const [error, setError] = useState<string | null>(null);

  const pageType = usePageType();
  const localize = useTypeSafeLocalize();

  // This hook is currently only used on the booking and edit-booking pages
  const { data: bookingShop } = useBookingShop(false);
  const { data: bookingEditShop } = useBookingEditShop(false);
  const { state: stateBooking, dispatch: dispatchBooking } = useContext(bookingState);
  const { state: stateBookingEdit, dispatch: dispatchBookingEdit } =
    useContext(bookingEditState);
  const trackBooking = useBookingTracking();
  const trackBookingEdit = useBookingEditTracking();

  let shop:
    | Booking_GetShopQuery['getShop']
    | BookingEdit_GetBookingQuery['getBooking']['shop'];
  let state: BookingStateCombinedState['state'] | BookingEditStateCombinedState['state'];
  let dispatch:
    | BookingStateCombinedState['dispatch']
    | BookingEditStateCombinedState['dispatch'];
  let track: typeof trackBooking | typeof trackBookingEdit;

  switch (pageType) {
    case 'booking': {
      shop = bookingShop;
      state = stateBooking;
      dispatch = dispatchBooking;
      track = trackBooking;
      break;
    }
    case 'edit-booking': {
      shop = bookingEditShop;
      state = stateBookingEdit;
      dispatch = dispatchBookingEdit;
      track = trackBookingEdit;
      break;
    }
    default: {
      state = stateBooking;
      dispatch = dispatchBooking;
      track = trackBooking;
      break;
    }
  }

  // Reset item count and clear error when navigating to another step
  useEffect(() => {
    setItemCount(1);
    setError(null);
  }, [state.currentStep]);

  const updateItemCount = (newCount: number) => {
    setItemCount(newCount);

    let existingUnits = 0;

    for (const orderItem of state.order) {
      const matchingItem = shop?.items.find(
        (item: { id: any }) => item.id === orderItem.itemId
      );
      if (matchingItem) {
        existingUnits += (matchingItem.alcoholUnits || 0) * orderItem.quantity;
      }
    }

    const isAlcoholValid = validateAlcoholUnits(
      state.booking.boatCount,
      (item.alcoholUnits || 0) * newCount,
      existingUnits
    );

    if (isAlcoholValid) {
      setError(null);
    } else {
      setError(localize(LocalizationCategory.error, ErrorLocalization.Alcoholerror));
    }
  };

  const addItem = async () => {
    try {
      let existingUnits = 0;

      for (const orderItem of state.order) {
        const matchingItem = shop?.items.find(
          (item: { id: any }) => item.id === orderItem.itemId
        );
        if (matchingItem) {
          existingUnits += (matchingItem.alcoholUnits || 0) * orderItem.quantity;
        }
      }

      const isAlcoholValid = validateAlcoholUnits(
        state.booking.boatCount,
        (item.alcoholUnits || 0) * itemCount,
        existingUnits
      );

      if (!isAlcoholValid) {
        setError(localize(LocalizationCategory.error, ErrorLocalization.Alcoholerror));
        return;
      } else {
        setLoading(true);
        setError(null);
      }

      const newItems = state.order.map((order: StateOrder) => ({
        itemId: order.itemId,
        quantity: order.quantity,
      }));

      const existingItemEntry = newItems.find((existingItem: StateOrder) => {
        return item.itemId === existingItem.itemId;
      });

      if (existingItemEntry) {
        existingItemEntry.quantity += itemCount;
      } else {
        newItems.push({
          quantity: itemCount,
          itemId: item.itemId,
        });
      }

      /**
       * Fire correct mutation for booking if we are on booking page
       */
      if (pageType === 'booking' && state.booking.id) {
        const result = await graphqlSdk.setBookingAddons({
          bookingId: state.booking.id,
          items: newItems,
        });

        const updatedOrder = result.setBookingAddons.addons.map((addon) => ({
          itemId: addon.itemId,
          quantity: addon.quantity,
        }));

        dispatch({
          type: orderReducerActionTypes.UPDATE_ORDER,
          payload: updatedOrder,
        });
        /**
         * Else fire mutation for reservation
         */
      } else if (pageType === 'edit-booking' && state.booking.reservationId) {
        const result = await graphqlSdk.setReservtionItems({
          reservationId: state.booking.reservationId,
          items: newItems,
        });

        const updatedOrder = result.setReservationItems.map((item) => ({
          itemId: item.itemId,
          quantity: item.quantity,
        }));

        dispatch({
          type: orderReducerActionTypes.UPDATE_ORDER,
          payload: updatedOrder,
        });
      }

      setLoading(false);
      if (window.location.pathname !== '/booking-reskin') {
        dispatch({ type: isSummaryOpenActionTypes.SET_IS_OPEN, payload: true });
      }
      track({ type: 'ADD_ITEM_TO_CART', item: { id: item.itemId, quantity: itemCount } });
      setItemCount(1);
    } catch (error) {
      setLoading(false);

      if (checkErrorCode(error, AddonsErrorCode.RentalItemAvailability)) {
        setError(
          localize(
            LocalizationCategory.error,
            ErrorLocalization.AddItemRentalItemUnavailable
          )
        );
      } else {
        setError(localize(LocalizationCategory.error, ErrorLocalization.UnknownError));
      }
    }
  };

  return {
    error,
    itemCount,
    setItemCount: updateItemCount,
    loading,
    addItem,
  };
};

export default useAddItem;
